import { Wallet } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { addDays, dueTransactionsHref } from '@/lib/hoje'
import { dueInLabel, formatBRL } from '@/lib/finance'
import { financeSummaryService, type DueAlerts, type UpcomingEntry } from '@/services/financeSummaryService'
import { EmptyLine, HojeSection } from './HojeSection'
import { useHojeLoader } from './useHojeLoader'

export const FINANCE_DUE_DAYS = 3

function EntryList({ title, entries }: { title: string; entries: UpcomingEntry[] }) {
  if (entries.length === 0) return null
  return (
    <section aria-label={title}>
      <h3 className="mb-1 text-sm font-semibold">{title} <span className="text-muted-foreground">({entries.length})</span></h3>
      <ul className="flex flex-col divide-y">
        {entries.map((e) => (
          <li key={e.id} className="grid grid-cols-[1fr_auto] items-center gap-2 py-1.5 text-sm">
            <span className="min-w-0">
              <span className="block truncate">{e.description}</span>
              <span className="text-xs text-muted-foreground">{dueInLabel(e.daysUntilDue)}</span>
            </span>
            <span className="flex items-center gap-1.5">
              {e.needsConfirmation && <Badge variant="secondary">Hermes</Badge>}
              <span className="tabular-nums">{formatBRL(e.amount)}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function OverdueLine({ overdue }: { overdue: DueAlerts['overdue'] }) {
  const { receivable, payable } = overdue
  if (receivable.count === 0 && payable.count === 0) return null
  return (
    <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
      Vencidos: {receivable.count} a receber ({formatBRL(receivable.total)}) · {payable.count} a pagar ({formatBRL(payable.total)})
    </p>
  )
}

/** Vencimentos financeiros em até 3 dias (a receber e a pagar). Some sem permissão. */
export function HojeFinanceiro() {
  const state = useHojeLoader(() => financeSummaryService.dueAlerts(FINANCE_DUE_DAYS))
  const href = state.status === 'ok' ? dueTransactionsHref(addDays(state.data.today, state.data.days)) : '/finance-v2/transactions?status=OPEN'

  return (
    <HojeSection title="Vencimentos" description={`A vencer em até ${FINANCE_DUE_DAYS} dias`} icon={Wallet} href={href} state={state}>
      {(alerts) => (
        <div className="flex flex-col gap-3">
          {alerts.receivable.length === 0 && alerts.payable.length === 0 && (
            <EmptyLine>Nada a vencer nos próximos {alerts.days} dias.</EmptyLine>
          )}
          <EntryList title="A receber" entries={alerts.receivable} />
          <EntryList title="A pagar" entries={alerts.payable} />
          <OverdueLine overdue={alerts.overdue} />
        </div>
      )}
    </HojeSection>
  )
}
